import { Injectable } from "@angular/core";
import { CompaniesService, DCCompanyType } from "../../services/companies/companies.service";
import { map, Observable, of, takeUntil } from "rxjs";
import { HorizontalBarType } from "../components/generic-listing/generic-listing.component";
import { PodInteraction, Stage } from "../models/pod-filter";
import { interactions } from "../models/auxiliary-company-models";
import { CompaniesSearchService } from "src/app/services/companies-search/companies-search.service";
import { RouterService } from "src/app/services/router/router.service";


export const timeline: { id: number; dispalyName: string, count: number }[] = [
  { id: 30, dispalyName: "30 Days", count: 30 },
  { id: 20, dispalyName: "20 Days", count: 20 },
  { id: 15, dispalyName: "15 Days", count: 15 },
  { id: 10, dispalyName: "10 Days", count: 10 },
  { id: 5, dispalyName: "5 Days", count: 5 },
];

@Injectable({
  providedIn: "root",
})
export class GenericListingService {
  constructor(
    private companiesService: CompaniesService,
    private companiesSearchService: CompaniesSearchService,
    private routerService: RouterService,
  ) {}

  fetchBarData(type: HorizontalBarType, ids: number[] | undefined, dcCompanyTypes?: DCCompanyType[]): Observable<any> {
    switch (type) {
      case HorizontalBarType.Stage:
        return this.fetchStageStats(ids);
      case HorizontalBarType.Interaction:
        return this.fetchInteractionStats(ids, dcCompanyTypes).pipe(
          map((data) => {
            return data.map((item) => {
              return {
                ...item,
                dispalyName: item.name,
                count: item.total,
              };
            });
          }),
        );
      case HorizontalBarType.Timeline:
        return this.fetchElevationTimelineStats().pipe(
          map((data) => {
            return data.map((item) => {
              return {
                ...item,
                dispalyName: item.name,
                count: item.count,
              };
            });
          }),
        );
      default:
        return new Observable();
    }
  }

private fetchStageStats(stageIds: number[] | undefined): Observable<any[]> {
  return this.companiesService.getCompanyStages(stageIds).pipe(
    takeUntil(this.routerService.destroy$),
    map((pods) => {
      if (pods.data) {
        const stages = pods.data.statuses.reverse();

        if (stages.length >= 3) {
          const mergedStage: Stage = {
            id: [stages[0].id, stages[1].id, stages[2].id], // Nested list of IDs
            name: `${stages[0].name} / ${stages[1].name} / ${stages[2].name}`,
            dispalyName: `${stages[0].dispalyName} / ${stages[1].dispalyName} / ${stages[2].dispalyName}`,
            ordinal: Math.min(stages[0].ordinal, stages[1].ordinal, stages[2].ordinal),
            count: stages[0].count + stages[1].count + stages[2].count,
            color: stages[0].color, // You can customize how to pick or combine colors
            tooltip: `${stages[0].tooltip || ''} ${stages[1].tooltip || ''} ${stages[2].tooltip || ''}`.trim(),
            selected: stages[0].selected || stages[1].selected || stages[2].selected,
            display: `${stages[0].display || ''} / ${stages[1].display || ''} / ${stages[2].display || ''}`.trim()
          };

          const restStages = stages.slice(3);
          return [mergedStage, ...restStages];
        }

        // If fewer than 3 elements, just return as is
        return stages;
      }

      return []; // Return an empty array if pods.data is undefined
    })
  );
}


  private fetchElevationTimelineStats(): Observable<any[]> {
    return this.companiesSearchService.getElevationTimeLineStats().pipe(
      takeUntil(this.routerService.destroy$),
      map((response) => {
        if (response.data) {
          return response.data?.timetable.reverse();
        }
        return []; // Return an empty array if pods.data is undefined
      }),
    );
  }

  private fetchInteractionStats(interactionInitiatorsInclude: number[] | undefined, dcCompanyTypes?: DCCompanyType[]): Observable<any[]> {
    return this.companiesService.getCompanyInteraction(interactionInitiatorsInclude, dcCompanyTypes).pipe(    
      takeUntil(this.routerService.destroy$),
      map((response) => {
        if (response?.data != null) {
          const data: PodInteraction = response.data;
          const int = interactions;
          Object.entries(data).forEach(([key, value]) => {
            int.forEach((interaction) => {
              if (interaction.label === key) {
                interaction.total = value;
              }
            });
          });
          return int;
        }
        return []; // Return an empty array if interactions.data is undefined
      }),
    );
  }
}
